import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, CreditCard, FileText, Loader2, Crown, Zap, Receipt, Shield, ShieldCheck, Menu, Key, Link2, ChevronDown, ChevronUp } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSubscription } from "@/hooks/useSubscription";
import { useCredits } from "@/hooks/useCredits";
import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MfaSetup } from "@/components/MfaSetup";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { DashboardLayout } from "@/components/DashboardLayout";

interface Payment {
  id: string;
  date: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  type: string;
}

const Account = () => {
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [extensionToken, setExtensionToken] = useState<string | null>(null);
  const [extensionDevices, setExtensionDevices] = useState<any[]>([]);
  const [showFullToken, setShowFullToken] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { subscription, features, refreshSubscription } = useSubscription();
  const { credits, refetch: refetchCredits } = useCredits();

  useEffect(() => {
    loadAccountData();
    loadPaymentHistory();
    checkMfaStatus();

    // Subscribe to realtime credit changes
    const creditsChannel = supabase
      .channel('account-credits-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'credits_ledger',
        },
        () => {
          console.log('[ACCOUNT] Credits changed, refetching...');
          refetchCredits();
        }
      )
      .subscribe();

    // Listen for manual credit update events
    const handleCreditsUpdate = () => {
      console.log('[ACCOUNT] Manual credit update event received');
      refetchCredits();
    };
    window.addEventListener('creditsUpdated', handleCreditsUpdate);

    return () => {
      supabase.removeChannel(creditsChannel);
      window.removeEventListener('creditsUpdated', handleCreditsUpdate);
    };
  }, []);

  useEffect(() => {
    if (user) {
      loadExtensionData();
    }
  }, [user]);

  const loadAccountData = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      setUser(session.user);
      
      // Force refresh subscription and credits
      await refreshSubscription();
      await refetchCredits();
    } catch (error) {
      console.error("Error loading account:", error);
      toast({
        title: "Error",
        description: "Failed to load account data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadExtensionData = async () => {
    if (!user) return;
    try {
      // Load extension token
      const { data: tokens } = await supabase
        .from('extension_tokens')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (tokens && tokens.length > 0) {
        setExtensionToken(tokens[0].token);
      }

      // Load extension devices
      const { data: devices } = await supabase
        .from('extension_devices')
        .select('*')
        .eq('user_id', user.id)
        .order('last_seen', { ascending: false });
      
      if (devices) {
        setExtensionDevices(devices);
      }
    } catch (error) {
      console.error('Error loading extension data:', error);
    }
  };

  const generateExtensionToken = async () => {
    try {
      setTokenLoading(true);
      const newToken = crypto.randomUUID();
      
      const { error } = await supabase
        .from('extension_tokens')
        .insert({
          user_id: user.id,
          token: newToken,
          is_active: true
        });

      if (error) throw error;

      setExtensionToken(newToken);
      toast({
        title: "Token Generated",
        description: "Your extension token has been created successfully.",
      });
    } catch (error) {
      console.error('Error generating token:', error);
      toast({
        title: "Error",
        description: "Failed to generate extension token",
        variant: "destructive",
      });
    } finally {
      setTokenLoading(false);
    }
  };

  const regenerateExtensionToken = async () => {
    try {
      setTokenLoading(true);
      
      // Mark old token as inactive
      await supabase
        .from('extension_tokens')
        .update({ is_active: false })
        .eq('user_id', user.id)
        .eq('is_active', true);

      // Generate new token
      await generateExtensionToken();
    } catch (error) {
      console.error('Error regenerating token:', error);
      toast({
        title: "Error",
        description: "Failed to regenerate extension token",
        variant: "destructive",
      });
    } finally {
      setTokenLoading(false);
    }
  };

  const copyTokenToClipboard = async () => {
    if (!extensionToken) return;
    try {
      await navigator.clipboard.writeText(extensionToken);
      toast({
        title: "Copied",
        description: "Extension token copied to clipboard",
      });
    } catch (error) {
      console.error('Error copying token:', error);
      toast({
        title: "Error",
        description: "Failed to copy token",
        variant: "destructive",
      });
    }
  };

  const removeDevice = async (deviceId: string) => {
    try {
      const { error } = await supabase
        .from('extension_devices')
        .update({ status: 'inactive' })
        .eq('id', deviceId);

      if (error) throw error;

      setExtensionDevices(devices => devices.filter(d => d.id !== deviceId));
      toast({
        title: "Device Removed",
        description: "The device has been marked as inactive.",
      });
    } catch (error) {
      console.error('Error removing device:', error);
      toast({
        title: "Error",
        description: "Failed to remove device",
        variant: "destructive",
      });
    }
  };

  const formatLastSeen = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  const loadPaymentHistory = async () => {
    try {
      setPaymentsLoading(true);
      const { data, error } = await supabase.functions.invoke("payment-history");

      if (error) throw error;

      if (data?.payments) {
        setPayments(data.payments);
      }
    } catch (error) {
      console.error("Error loading payment history:", error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const checkMfaStatus = async () => {
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      // Check if there are any VERIFIED factors
      const hasVerifiedFactors = data?.all?.some(f => f.status === 'verified') || false;
      setMfaEnabled(hasVerifiedFactors);
    } catch (error) {
      console.error("Error checking MFA status:", error);
    }
  };

  const disableMfa = async () => {
    try {
      setMfaLoading(true);
      const { data } = await supabase.auth.mfa.listFactors();
      
      // Find all verified factors
      const verifiedFactors = data?.all?.filter(f => f.status === 'verified') || [];
      
      if (verifiedFactors.length > 0) {
        // Unenroll all verified factors
        for (const factor of verifiedFactors) {
          const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
          if (error) throw error;
        }
        
        setMfaEnabled(false);
        toast({
          title: "Success",
          description: "Two-factor authentication has been disabled",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to disable 2FA",
        variant: "destructive",
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const validatePassword = (pwd: string): boolean => {
    if (pwd.length < 8) {
      toast({
        title: "Invalid Password",
        description: "Password must be at least 8 characters long",
        variant: "destructive",
      });
      return false;
    }
    if (!/[A-Z]/.test(pwd)) {
      toast({
        title: "Invalid Password",
        description: "Password must contain at least one uppercase letter",
        variant: "destructive",
      });
      return false;
    }
    if (!/[a-z]/.test(pwd)) {
      toast({
        title: "Invalid Password",
        description: "Password must contain at least one lowercase letter",
        variant: "destructive",
      });
      return false;
    }
    if (!/[0-9]/.test(pwd)) {
      toast({
        title: "Invalid Password",
        description: "Password must contain at least one number",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword(newPassword)) {
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are identical",
        variant: "destructive",
      });
      return;
    }

    setPasswordLoading(true);

    try {
      // First verify current password by attempting to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error("Current password is incorrect");
      }

      // Update to new password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Your password has been updated successfully",
      });

      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Password change error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setPasswordLoading(false);
    }
  };


  const syncSubscription = async () => {
    try {
      setSyncLoading(true);
      const { data, error } = await supabase.functions.invoke("sync-stripe-subscription");

      if (error) throw error;

      // Refresh credits and subscription
      await refetchCredits();
      await refreshSubscription();

      toast({
        title: "Subscription Synced",
        description: `Your ${data.planType} plan is now active with ${credits?.quota || 0} credits`,
      });
    } catch (error: any) {
      console.error("Sync error:", error);
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync subscription. Please contact support if this persists.",
        variant: "destructive",
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    try {
      setPortalLoading(true);
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Portal error:", error);
      toast({
        title: "Error",
        description: "Failed to open billing portal",
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="mb-8 h-8 w-48" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Account & Billing</h1>
          <p className="text-muted-foreground">Manage your account settings and billing information</p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Credits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Your Credits
                <Badge variant="secondary" className="ml-auto">
                  Live
                </Badge>
              </CardTitle>
              <CardDescription>AI feature credits with real-time updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Credit Balance - Large Display */}
              <div className="text-center p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-lg border border-primary/20">
                <div className="text-5xl font-bold text-primary mb-2">
                  {credits?.remaining || 0}
                </div>
                <div className="text-sm text-muted-foreground mb-1">
                  of {credits?.quota || 0} credits available
                </div>
                {credits?.next_reset_at && (
                  <div className="text-xs text-muted-foreground mt-2">
                    Resets {new Date(credits.next_reset_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>

              {/* Usage Breakdown */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm font-medium">Credits Used</span>
                  <Badge variant="outline" className="text-base">
                    {credits?.used || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm font-medium">Plan Type</span>
                  <Badge variant="default" className="text-base capitalize">
                    <Crown className="mr-1 h-3 w-3" />
                    {credits?.plan || 'free'}
                  </Badge>
                </div>
              </div>

              {/* Credit Costs Info */}
              <div className="pt-4 border-t space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Cost per Action</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="font-bold text-base">1</div>
                    <div className="text-muted-foreground">Resume/Letter</div>
                  </div>
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="font-bold text-base">2</div>
                    <div className="text-muted-foreground">Pro Features</div>
                  </div>
                  <div className="p-2 bg-muted/50 rounded text-center">
                    <div className="font-bold text-base">5</div>
                    <div className="text-muted-foreground">Premium</div>
                  </div>
                </div>
                {credits?.plan === 'premium' && (
                  <div className="mt-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                    <p className="text-xs font-semibold text-primary flex items-center gap-2">
                      <Crown className="h-3.5 w-3.5" />
                      Premium Benefit: Unlimited 1-2 credit actions!
                    </p>
                  </div>
                )}
              </div>

              {/* Sync and Manage Buttons */}
              <div className="pt-4 border-t space-y-2">
                <Button
                  onClick={syncSubscription}
                  disabled={syncLoading}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  {syncLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      Sync Subscription
                    </>
                  )}
                </Button>
                
                {subscription && subscription.subscribed && (
                  <Button
                    onClick={openCustomerPortal}
                    disabled={portalLoading}
                    variant="default"
                    className="w-full"
                    size="sm"
                  >
                    {portalLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Manage Billing
                      </>
                    )}
                  </Button>
                )}
                
                {(!subscription || !subscription.subscribed) && (
                  <Link to="/pricing">
                    <Button className="w-full" size="sm">
                      <Crown className="mr-2 h-4 w-4" />
                      Upgrade Plan
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Your account details and security settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="font-medium">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Member Since</span>
                <span className="font-medium">
                  {new Date(user?.created_at).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Chrome Extension */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Chrome Extension
              </CardTitle>
              <CardDescription>Manage your extension authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Token Section */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Extension Token</Label>
                  {!extensionToken && (
                    <Button 
                      onClick={generateExtensionToken} 
                      disabled={tokenLoading}
                      size="sm"
                    >
                      {tokenLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        'Generate Token'
                      )}
                    </Button>
                  )}
                </div>
                
                {extensionToken && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={showFullToken ? extensionToken : `${extensionToken.slice(0, 8)}...${extensionToken.slice(-4)}`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFullToken(!showFullToken)}
                      >
                        {showFullToken ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyTokenToClipboard}
                      >
                        Copy
                      </Button>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={regenerateExtensionToken}
                      disabled={tokenLoading}
                    >
                      {tokenLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Regenerating...
                        </>
                      ) : (
                        'Regenerate Token'
                      )}
                    </Button>
                  </div>
                )}
              </div>

              {/* Registered Devices */}
              {extensionDevices.length > 0 && (
                <Collapsible
                  open={devicesOpen}
                  onOpenChange={setDevicesOpen}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <Label>Registered Devices ({extensionDevices.length})</Label>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="w-9 p-0">
                        {devicesOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                        <span className="sr-only">Toggle devices</span>
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  
                  <CollapsibleContent className="space-y-2">
                    {extensionDevices.map((device) => (
                      <div
                        key={device.id}
                        className="flex items-center justify-between p-3 border border-border rounded-lg bg-background/50"
                      >
                        <div className="space-y-1">
                          <div className="font-medium">{device.name}</div>
                          <div className="text-sm text-muted-foreground">
                            Last active: {formatLastSeen(device.last_seen)}
                          </div>
                          <Badge variant={device.status === 'active' ? 'default' : 'secondary'}>
                            {device.status}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDevice(device.id)}
                          disabled={device.status !== 'active'}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    disabled={passwordLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={passwordLoading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters with uppercase, lowercase, and numbers
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={passwordLoading}
                  />
                </div>
                <Button type="submit" disabled={passwordLoading}>
                  {passwordLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Two-Factor Authentication */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account with 2FA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  {mfaEnabled ? (
                    <ShieldCheck className="h-5 w-5 text-green-500" />
                  ) : (
                    <Shield className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">
                      {mfaEnabled ? "2FA is enabled" : "2FA is disabled"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {mfaEnabled 
                        ? "Your account is protected with two-factor authentication" 
                        : "Secure your account by enabling two-factor authentication"
                      }
                    </p>
                  </div>
                </div>
                <Badge variant={mfaEnabled ? "default" : "secondary"}>
                  {mfaEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              
              {mfaEnabled ? (
                <Button
                  variant="destructive"
                  onClick={disableMfa}
                  disabled={mfaLoading}
                  className="w-full"
                >
                  {mfaLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    "Disable 2FA"
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => setMfaSetupOpen(true)}
                  className="w-full"
                >
                  <Shield className="mr-2 h-4 w-4" />
                  Enable 2FA
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Connected Accounts */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Connected Accounts
              </CardTitle>
              <CardDescription>
                Manage your job board account connections for seamless auto-apply
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Link2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Job Board Accounts</p>
                    <p className="text-sm text-muted-foreground">
                      Connect Indeed, LinkedIn, ZipRecruiter and more
                    </p>
                  </div>
                </div>
                <Badge variant="secondary">Encrypted</Badge>
              </div>
              
              <Link to="/app/account/connections">
                <Button className="w-full">
                  <Link2 className="mr-2 h-4 w-4" />
                  Manage Connected Accounts
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Payment History
              </CardTitle>
              <CardDescription>Your recent transactions and invoices</CardDescription>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : payments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>
                          {new Date(payment.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{payment.description}</TableCell>
                        <TableCell>
                          ${payment.amount.toFixed(2)} {payment.currency}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              payment.status === "succeeded" || payment.status === "paid"
                                ? "default"
                                : payment.status === "pending"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {payment.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-8">
                  No payment history yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <MfaSetup
        open={mfaSetupOpen}
        onOpenChange={setMfaSetupOpen}
        onSuccess={checkMfaStatus}
      />
    </DashboardLayout>
  );
};

export default Account;
